import { useEffect, useRef, useState } from 'react'
import { motion, useAnimation } from 'framer-motion'

// A global, fixed background lamp with a pull string that toggles theme when pulled.
// It stays behind all pages but is interactive.
function LampPull({ theme, onToggle }) {
  const [pulled, setPulled] = useState(false)
  const controls = useAnimation()
  const yRef = useRef(0)

  useEffect(() => {
    // Glow animation based on theme
    controls.start(theme === 'dark' ? { opacity: 0.15 } : { opacity: 0.4 })
  }, [theme, controls])

  const handleDrag = (_, info) => {
    yRef.current = info.point.y
  }

  const handleDragEnd = (_, info) => {
    const deltaY = info.offset.y
    if (deltaY > 60) {
      setPulled(true)
      onToggle()
      setTimeout(() => setPulled(false), 600)
    }
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-0">
      {/* Ambient gradient background with smooth transition */}
      <motion.div
        className="absolute inset-0"
        animate={{ background: theme === 'dark'
            ? 'radial-gradient(1200px circle at top right, rgba(255, 216, 0, 0.15), transparent 60%), linear-gradient(to bottom, #0f172a, #0b1220)'
            : 'radial-gradient(900px circle at top right, rgba(255, 216, 0, 0.35), transparent 60%), linear-gradient(to bottom, #f8fafc, #eef2ff)'
          }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Lamp and cord */}
      <div className="absolute right-10 top-0">
        <svg width="140" height="300" viewBox="0 0 140 300" className="overflow-visible">
          {/* Cord */}
          <line x1="70" y1="0" x2="70" y2="160" stroke="#999" strokeWidth="2" strokeDasharray="4 4" />
          {/* Lamp head */}
          <circle cx="70" cy="190" r="26" fill={theme === 'dark' ? '#1f2937' : '#f8fafc'} stroke="#999" strokeWidth="1.5" />
          {/* Bulb glow */}
          <motion.circle
            cx="70"
            cy="190"
            r="40"
            initial={{ opacity: 0.2 }}
            animate={controls}
            fill={theme === 'dark' ? 'rgba(255, 216, 0, 0.15)' : 'rgba(255, 216, 0, 0.4)'}
          />
          {/* Pull ring */}
          <motion.circle
            cx={70}
            cy={145}
            r={10}
            className="pointer-events-auto cursor-grab"
            fill={theme === 'dark' ? '#eab308' : '#0ea5e9'}
            stroke="#333"
            strokeWidth={1}
            drag="y"
            dragConstraints={{ top: 0, bottom: 90 }}
            dragElastic={0.15}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            whileTap={{ scale: 0.95 }}
            initial={{ cy: 145 }}
            animate={{ cy: pulled ? 165 : 145 }}
            transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          />
          {/* String connecting ring to lamp */}
          <line x1="70" y1="160" x2="70" y2={pulled ? 170 : 155} stroke="#bbb" strokeWidth="2" />
        </svg>
      </div>
    </div>
  )
}

export default LampPull
